import React from 'react'
import { Badge } from '../shared/Badge'
import { formatWaitTime, formatRelativeTime } from '../../utils/format'
import { QueueChat } from '../../hooks/useQueue'
import { isObservingChat } from '../../hooks/useWhisperMode'
import { safeInitial, safeString } from '../../utils/safeString'
import { countryToFlag } from '../../utils/countryFlag'
import { topicalTags } from '../../utils/chatTags'

/* ─── Animated speaker icon ─────────────────────────────────────────── */

const SpeakerIcon: React.FC = () => (
  <div className="relative flex items-center justify-center" title="Alert playing">
    <svg
      className="w-4 h-4 text-warning animate-pulse"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z"
      />
    </svg>
  </div>
)

/* ─── AI First Responder state badge (read-only) ────────────────────────── */

const AI_BADGE: Record<string, { label: string; cls: string }> = {
  active: { label: 'AI handling', cls: 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300' },
  escalated: { label: 'AI escalated', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  closed_by_ai: { label: 'AI resolved', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  abandoned: { label: 'AI abandoned', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
  suppressed: { label: 'Human only', cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' },
}

const AiStateBadge: React.FC<{ state?: string | null }> = ({ state }) => {
  if (!state) return null
  const b = AI_BADGE[state]
  if (!b) return null
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${b.cls}`}
      title="AI First Responder"
    >
      {b.label}
    </span>
  )
}

/** Only surface a plan badge for paying tiers — "free"/"starter"/unknown are
 *  noise on every row. (WhatsApp is already shown by the avatar.) */
const PAID_PLANS = new Set(['pro', 'professional', 'enterprise'])
function isPaidPlan(plan?: string | null): boolean {
  return !!plan && PAID_PLANS.has(String(plan).toLowerCase())
}

interface QueueItemProps {
  chat: QueueChat
  isSelected: boolean
  isAlerting?: boolean
  onClick: () => void
  onPickup?: () => void
  isPickingUp?: boolean
  onRestore?: () => void
}

export const QueueItem: React.FC<QueueItemProps> = ({
  chat,
  isSelected,
  isAlerting,
  onClick,
  onPickup,
  isPickingUp,
  onRestore,
}) => {
  return (
    <div
      onClick={onClick}
      className={`group relative px-4 py-3.5 border-b border-border cursor-pointer transition-all duration-100 ${
        isAlerting
          ? 'bg-warning/5 dark:bg-warning/10 border-l-2 border-l-warning'
          : isSelected
            ? 'bg-brand-50/60 dark:bg-brand-900/20 border-l-2 border-l-brand-600'
            : 'hover:bg-surface-hover border-l-2 border-l-transparent'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar — company logo is the main thumbnail (fallback to a company /
            person initial); WhatsApp chats keep the WA mark. */}
        <div className="shrink-0 relative">
          {chat.channel !== 'whatsapp' && chat.companyLogoUrl ? (
            <img
              src={chat.companyLogoUrl}
              alt=""
              className="w-9 h-9 rounded-full object-cover bg-surface-secondary border border-border"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
            />
          ) : (
            <div className={`flex items-center justify-center w-9 h-9 rounded-full text-sm font-semibold ${
              chat.channel === 'whatsapp'
                ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400'
                : 'bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400'
            }`}>
              {chat.channel === 'whatsapp' ? (
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              ) : (
                safeInitial(chat.companyName || chat.visitorName || chat.customerName)
              )}
            </div>
          )}
          {chat.channel === 'whatsapp' && (
            <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white dark:border-gray-900" title="WhatsApp" />
          )}
          {chat.channel !== 'whatsapp' && countryToFlag(chat.companyCountry) && (
            <span className="absolute -bottom-1 -right-1 text-xs leading-none drop-shadow-sm" title={chat.companyCountry}>
              {countryToFlag(chat.companyCountry)}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Top row: person name (big) + time */}
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <h4 className="text-sm font-semibold text-txt-primary truncate">
              {safeString(chat.visitorName || chat.customerName || chat.companyName, 'Unknown')}
            </h4>
            <span className="flex items-center gap-1.5 shrink-0">
              {isObservingChat(chat.id) && (
                <div className="flex items-center justify-center" title="You are monitoring this chat">
                  <svg className="w-3.5 h-3.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
              )}
              {isAlerting && <SpeakerIcon />}
              <span className="text-[11px] font-medium text-txt-secondary whitespace-nowrap">
                {formatRelativeTime(chat.lastMessageAt || chat.createdAt)}
              </span>
            </span>
          </div>

          {/* Company (widget chats — resolved from the URL alias) */}
          {chat.companyName && (
            <p className="text-xs font-medium text-txt-secondary truncate -mt-0.5 mb-1">{chat.companyName}</p>
          )}

          {/* Message preview — the latest message from either side */}
          {(() => {
            const latest = chat.latestMessage?.content?.trim()
            const preview = latest || chat.firstCustomerMessage
            if (!preview) {
              return <p className="text-xs text-txt-tertiary truncate mb-1.5 italic">No messages yet</p>
            }
            const mine = chat.latestMessage && (chat.latestMessage.sender === 'agent' || chat.latestMessage.sender === 'bot')
            return (
              <p className="text-xs text-txt-secondary truncate mb-1.5">
                {mine && <span className="text-txt-tertiary">You: </span>}
                {preview}
              </p>
            )
          })()}

          {/* Badges row — kept clean: only meaningful signals show. The neutral
              defaults (free plan, medium priority) are omitted so the row isn't
              a wall of colour; status + AI state always show, paid plan and
              high/low priority only when they matter, plus up to two tags. */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <AiStateBadge state={chat.aiState} />
            <Badge variant="status" value={chat.status} />
            {isPaidPlan(chat.plan) && <Badge variant="plan" value={chat.plan} />}
            {chat.priority && chat.priority.toLowerCase() !== 'medium' && (
              <Badge variant="priority" value={chat.priority} />
            )}
            {topicalTags(chat.tags).slice(0, 2).map((tag) => (
              <Badge key={tag} variant="tag" value={tag} />
            ))}
            {topicalTags(chat.tags).length > 2 && (
              <span className="text-[10px] text-txt-secondary font-semibold">
                +{topicalTags(chat.tags).length - 2}
              </span>
            )}
          </div>

          {/* Footer: wait time, messages, agent */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-3 text-[11px] text-txt-secondary">
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {formatWaitTime(chat.waitTime)}
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                </svg>
                {chat.messageCount}
              </span>
            </div>

            {onRestore ? (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onRestore()
                }}
                className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors opacity-0 group-hover:opacity-100"
              >
                Restore
              </button>
            ) : chat.assignedAgentName ? (
              <span className="text-[11px] text-txt-secondary font-medium">
                {chat.assignedAgentName}
              </span>
            ) : (onPickup && chat.status === 'queued') ? (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onPickup()
                }}
                disabled={isPickingUp}
                className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition-colors opacity-0 group-hover:opacity-100"
              >
                {isPickingUp ? 'Assigning...' : 'Pick up'}
              </button>
            ) : (
              <span className="text-[11px] text-warning font-medium">Unassigned</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
